import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations, DEFAULT_LANG, SUPPORTED_LANGS } from "@/i18n/translations";

const LanguageContext = createContext(null);

const STORAGE_KEY = "sk_lang";

function getInitialLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
  } catch {
    // ignore
  }
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(getInitialLang);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang);
      const dict = translations[lang];
      if (dict?.docTitle) document.title = dict.docTitle;
    }
  }, [lang]);

  const value = useMemo(() => {
    const dict = translations[lang] || translations[DEFAULT_LANG];
    return {
      lang,
      setLang,
      toggle: () =>
        setLang((l) => {
          const idx = SUPPORTED_LANGS.indexOf(l);
          return SUPPORTED_LANGS[(idx + 1) % SUPPORTED_LANGS.length];
        }),
      t: dict,
    };
  }, [lang]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useT() {
  const ctx = useContext(LanguageContext);
  if (!ctx)
    throw new Error("useT must be used within LanguageProvider");
  return ctx;
}
